import * as React from 'react';
import {
  Checkbox as RACCheckbox,
  type CheckboxProps as RACCheckboxProps,
  type CheckboxRenderProps,
} from 'react-aria-components';
import { CheckLine, SubtractLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret Checkbox (handoff §10.3).
 * 16×16, r1 (3px), 1px --tvl-border-input, --tvl-raised fill.
 * Checked = --tvl-accent fill + --tvl-on-accent check (CheckLine 10px).
 * Indeterminate = accent fill + 8px SubtractLine rule.
 * Focus = global 2px ink outline (globals.css) — no accent ring.
 */
export interface CheckboxProps extends Omit<RACCheckboxProps, 'children'> {
  /** Mark field as invalid (maps to aria-invalid). */
  invalid?: boolean;
  className?: string;
  /** Label content rendered next to the control box. */
  children?: React.ReactNode;
}

export const Checkbox = React.forwardRef<HTMLLabelElement, CheckboxProps>(
  ({ className, invalid, children, ...props }, ref) => (
    <RACCheckbox
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn('group inline-flex cursor-pointer items-center gap-2', className)}
      {...props}
    >
      {({ isSelected, isIndeterminate, isDisabled }: CheckboxRenderProps) => (
        <>
          {/* control box */}
          <span
            className={cn(
              'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[var(--tvl-r1)] border transition-colors duration-[var(--tvl-d-fast)]',
              // base
              'border-[var(--tvl-border-input)] bg-[var(--tvl-raised)]',
              // checked / indeterminate
              (isSelected || isIndeterminate) &&
                'border-[var(--tvl-accent)] bg-[var(--tvl-accent)]',
              // hover (pointer-only wash when unchecked)
              !isSelected && !isIndeterminate && 'group-hover:bg-[var(--tvl-wash-hover)]',
              // invalid
              invalid && !isSelected && !isIndeterminate && 'border-[var(--tvl-danger)]',
              // disabled
              isDisabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {isIndeterminate ? (
              <SubtractLine size={10} color="var(--tvl-on-accent)" aria-hidden />
            ) : isSelected ? (
              <CheckLine size={10} color="var(--tvl-on-accent)" aria-hidden />
            ) : null}
          </span>
          {/* label */}
          {children ? (
            <span
              className={cn(
                'text-sm leading-5 text-[var(--tvl-ink)]',
                isDisabled && 'text-[var(--tvl-ink-disabled)]',
              )}
            >
              {children}
            </span>
          ) : null}
        </>
      )}
    </RACCheckbox>
  ),
);
Checkbox.displayName = 'Checkbox';
